import { OptionListId } from "shared/hubrise/ids"
import { ICatalog, IOption, IOptionList, IProduct, ISku } from "shared/hubrise/models"
import * as Option from "shared/hubrise/utils/Option"
import {
  findById,
  insertItemInArray,
  moveItemWithinArray,
  removeInArrayById,
  removeItemInArray,
  replaceInArrayById,
} from "shared/utils/javascript"

import { updatedCatalog } from "./Catalog"

/**
 * Add an option list.
 */
export function add(
  catalog: ICatalog,
  insertAfter: IOptionList | undefined,
  values: Partial<IOptionList>,
  optionValues?: Partial<IOption>,
): { newCatalog: ICatalog; optionList: IOptionList } {
  const position: number = insertAfter
    ? catalog.data.optionLists.findIndex((optionList) => optionList.id === insertAfter.id) + 1
    : 0
  const id = OptionListId(catalog.idGenerator())
  const defaultValues: IOptionList = {
    // Keep the order of the properties same as the interface for objectHash to generate consistent hashes.
    id,
    name: "",
    min_selections: 0,
    max_selections: null,
    multiple_selection: false,
    tags: [],
    options: [{ ...Option.create(catalog), ...optionValues }],
  }
  const optionList: IOptionList = { ...defaultValues, ...values }
  const optionLists: Array<IOptionList> = insertItemInArray(catalog.data.optionLists, optionList, position)
  const newCatalog: ICatalog = updatedCatalog(catalog, { optionLists })
  return { newCatalog, optionList }
}

/**
 * Delete the option list. The function name is because delete is a reserved JS word.
 * @param catalog
 * @param optionListId
 */
export function doDelete(catalog: ICatalog, optionListId: OptionListId): ICatalog {
  return updatedCatalog(catalog, {
    products: catalog.data.products.map((product) => ({
      ...product,
      skus: product.skus.map((sku) => ({ ...sku, optionListIds: removeItemInArray(sku.optionListIds, optionListId) })),
    })),
    optionLists: removeInArrayById(catalog.data.optionLists, optionListId),
  })
}

/**
 * Return the option list matching the id.
 * @param catalog
 * @param id
 */
export const byId = (catalog: ICatalog, id: OptionListId): IOptionList => {
  const optionList = findById(catalog.data.optionLists, id)
  if (!optionList) throw `Option list ${id} not found`
  return optionList
}

/**
 * Return the option lists attached to the sku.
 * @param catalog
 * @param sku
 */
export const bySku = (catalog: ICatalog, sku: ISku): Array<IOptionList> => {
  return sku.optionListIds.map((id) => catalog.data.optionLists.find((ol) => ol.id === id)) as Array<IOptionList>
}

/**
 * Find all skus using this option list. Returns an array of [product, sku] tuples.
 * @param catalog
 * @param optionList
 */
export const findUsages = (catalog: ICatalog, optionList: IOptionList): Array<Usage> => {
  const usages: Array<Usage> = []
  catalog.data.products.forEach((product) => {
    product.skus
      .filter((sku) => sku.optionListIds.includes(optionList.id))
      .forEach((sku) => usages.push({ product, sku }))
  })
  return usages
}
type Usage = { product: IProduct; sku: ISku }

/**
 * Return true if the option list accepts a single mandatory option.
 * @param optionList
 */
export const isSingleChoice = (optionList: IOptionList): boolean => {
  return optionList.min_selections === 1 && optionList.max_selections === 1
}

/**
 * Return true if the option list can have repeatable options.
 * @param optionList
 */
export const canRepeatOptions = (optionList: IOptionList): boolean => {
  return optionList.max_selections === null || optionList.max_selections >= 2
}

/**
 * Move an option list to a new position.
 * @param catalog
 * @param optionList
 * @param newPosition
 */
export function move(catalog: ICatalog, optionList: IOptionList, newPosition: number): ICatalog {
  const optionLists: Array<IOptionList> = moveItemWithinArray(catalog.data.optionLists, optionList, newPosition)
  return updatedCatalog(catalog, { optionLists })
}

/**
 * Update an option list and return the resulting catalog.
 * @param catalog
 * @param id
 * @param fieldName
 * @param value
 */
export function update<T extends keyof IOptionList>(
  catalog: ICatalog,
  id: OptionListId,
  fieldName: T,
  value: IOptionList[T],
): ICatalog {
  return updatedCatalog(catalog, {
    optionLists: replaceInArrayById(catalog.data.optionLists, id, (optionList) => ({
      ...optionList,
      [fieldName]: value,
    })),
  })
}

/**
 * Return the option list's ref, which is the option list's id as a string.
 * @param optionList
 */
export const ref = (optionList: IOptionList): string => String(optionList.id)
